import apiClient from '../client';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// Types
export type Conversation = {
  id: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  participants: ConversationParticipant[];
};

export type ConversationParticipant = {
  id: string;
  conversation_id: string;
  user_id: string;
  role: 'genius' | 'client';
  joined_at: string;
  last_read_at: string | null;
  unread_count: number;
  display_name?: string | null;
  profile_picture_url?: string | null;
};

export type Message = {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  message_type: 'text' | 'file';
  file_url: string | null;
  file_name: string | null;
  file_type: string | null;
  caption: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  is_read?: boolean;
  read_at?: string | null;
};

export type MessageInsert = {
  content?: string;
  message_type?: 'text' | 'file';
  file_url?: string | null;
  file_name?: string | null;
  file_type?: string | null;
  caption?: string | null;
};

export type FileUploadResponse = {
  url: string;
  file_name: string;
  file_type: string;
  file_size: number;
};

export const chatService = {
  /**
   * Get all conversations for the current user
   */
  getConversations: async (): Promise<Conversation[]> => {
    const result = await apiClient.get<Conversation[]>('/chat/conversations');
    return result as unknown as Conversation[];
  },

  /**
   * Get a conversation by ID
   */
  getConversation: async (conversationId: string): Promise<Conversation> => {
    const result = await apiClient.get<Conversation>(`/chat/conversations/${conversationId}`);
    return result as unknown as Conversation;
  },

  /**
   * Create or find existing conversation with another user
   */
  createOrFindConversation: async (otherUserId: string): Promise<Conversation> => {
    const result = await apiClient.post<Conversation>('/chat/conversations', {
      other_user_id: otherUserId,
    });
    return result as unknown as Conversation;
  },

  /**
   * Get messages for a conversation (paginated)
   */
  getMessages: async (
    conversationId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<{
    messages: Message[];
    pagination: {
      limit: number;
      offset: number;
      has_more: boolean;
      total: number;
    };
  }> => {
    const result = await apiClient.get<{
      messages: Message[];
      pagination: {
        limit: number;
        offset: number;
        has_more: boolean;
        total: number;
      };
    }>(`/chat/conversations/${conversationId}/messages`, {
      params: { limit, offset },
    }) as unknown as {
      messages: Message[];
      pagination: {
        limit: number;
        offset: number;
        has_more: boolean;
        total: number;
      };
    };
    return result;
  },

  /**
   * Send a message in a conversation
   */
  sendMessage: async (
    conversationId: string,
    messageData: MessageInsert
  ): Promise<Message> => {
    const result = await apiClient.post<Message>(
      `/chat/conversations/${conversationId}/messages`,
      messageData
    );
    return result as unknown as Message;
  },

  /**
   * Mark a message as read
   */
  markAsRead: async (messageId: string): Promise<void> => {
    await apiClient.post(`/chat/messages/${messageId}/read`);
  },

  /**
   * Mark multiple messages as read
   */
  markMultipleAsRead: async (messageIds: string[]): Promise<void> => {
    await apiClient.post('/chat/messages/read', {
      message_ids: messageIds,
    });
  },

  /**
   * Update typing indicator
   */
  setTyping: async (conversationId: string, isTyping: boolean): Promise<void> => {
    await apiClient.post(`/chat/conversations/${conversationId}/typing`, {
      is_typing: isTyping,
    });
  },

  /**
   * Search messages in a conversation
   */
  searchMessages: async (
    conversationId: string,
    query: string,
    limit: number = 50
  ): Promise<Message[]> => {
    const result = await apiClient.get<Message[]>(
      `/chat/conversations/${conversationId}/search`,
      {
        params: { query, limit },
      }
    );
    return result as unknown as Message[];
  },

  /**
   * Get unread count for a conversation
   */
  getUnreadCount: async (conversationId: string): Promise<number> => {
    const result = await apiClient.get<{ unread_count: number }>(
      `/chat/conversations/${conversationId}/unread`
    );
    return (result as unknown as { unread_count: number }).unread_count;
  },

  /**
   * Delete a message
   */
  deleteMessage: async (messageId: string): Promise<void> => {
    await apiClient.delete(`/chat/messages/${messageId}`);
  },

  /**
   * Upload a file attachment
   */
  uploadFile: async (
    file: File,
    getToken: () => Promise<string | null>,
    conversationId?: string
  ): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (conversationId) {
      formData.append('conversation_id', conversationId);
    }

    const token = await getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await axios.post<{ success: boolean; data: FileUploadResponse }>(
      `${API_URL}/chat/upload`,
      formData,
      {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data.success) {
      return response.data.data;
    }
    throw new Error('File upload failed');
  },

  /**
   * Get AI suggestions for a conversation (returns suggestions and analysis)
   */
  getAISuggestions: async (conversationId: string): Promise<{
    suggestions: string[];
    analysis?: {
      overall_sentiment: 'positive' | 'neutral' | 'negative';
      sentiment_score: number;
      average_sentiment: number;
      ai_tags: string[];
      is_flagged: boolean;
      flag_reason: string | null;
      flag_details: Record<string, any> | null;
    };
  }> => {
    const result = await apiClient.post<{
      suggestions: string[];
      analysis: {
        overall_sentiment: 'positive' | 'neutral' | 'negative';
        sentiment_score: number;
        average_sentiment: number;
        ai_tags: string[];
        is_flagged: boolean;
        flag_reason: string | null;
        flag_details: Record<string, any> | null;
      };
    }>(`/chat/conversations/${conversationId}/ai-suggestions`);
    return result as unknown as {
      suggestions: string[];
      analysis?: {
        overall_sentiment: 'positive' | 'neutral' | 'negative';
        sentiment_score: number;
        average_sentiment: number;
        ai_tags: string[];
        is_flagged: boolean;
        flag_reason: string | null;
        flag_details: Record<string, any> | null;
      };
    };
  },

  /**
   * Get AI helper response
   */
  getAIHelperResponse: async (
    question: string,
    conversationId?: string
  ): Promise<{ response: string }> => {
    const result = await apiClient.post<{ response: string }>('/chat/ai-helper', {
      question,
      conversation_id: conversationId,
    });
    return result as unknown as { response: string };
  },

  /**
   * Get contextual tips for a conversation
   */
  getContextualTips: async (conversationId: string): Promise<Array<{ title: string; icon: string; content: string }>> => {
    const result = await apiClient.post<Array<{ title: string; icon: string; content: string }>>(
      `/chat/conversations/${conversationId}/ai-helper/tips`
    );
    return result as unknown as Array<{ title: string; icon: string; content: string }>;
  },

  /**
   * Get user preferences
   */
  getUserPreferences: async (): Promise<{
    id: string;
    user_id: string;
    tips_enabled: boolean;
    helper_collapsed: boolean;
    suggestions_collapsed: boolean;
    created_at: string;
    updated_at: string;
  }> => {
    const result = await apiClient.get('/chat/user/preferences');
    return result as unknown as {
      id: string;
      user_id: string;
      tips_enabled: boolean;
      helper_collapsed: boolean;
      suggestions_collapsed: boolean;
      created_at: string;
      updated_at: string;
    };
  },

  /**
   * Update user preferences
   */
  updateUserPreferences: async (updates: {
    tips_enabled?: boolean;
    helper_collapsed?: boolean;
    suggestions_collapsed?: boolean;
  }): Promise<{
    id: string;
    user_id: string;
    tips_enabled: boolean;
    helper_collapsed: boolean;
    suggestions_collapsed: boolean;
    created_at: string;
    updated_at: string;
  }> => {
    const result = await apiClient.patch('/chat/user/preferences', updates);
    return result as unknown as {
      id: string;
      user_id: string;
      tips_enabled: boolean;
      helper_collapsed: boolean;
      suggestions_collapsed: boolean;
      created_at: string;
      updated_at: string;
    };
  },
};
